import { redirect } from '@tanstack/react-router';
import type { AuthContextType } from '@/features/auth';
import * as authLib from '@/lib/auth';

/**
 * Shared authentication guards for route protection
 */

type ProtectedRouteRole = 'ADMIN' | 'OPERATIONS' | 'CLIENT' | 'SALES' | 'GM';

interface ProtectedRouteOptions {
  /**
   * Whether authentication is required
   * @default true
   */
  requireAuth?: boolean;
  /**
   * Required role(s) to access the route
   * If multiple roles are provided, user must have at least one
   */
  roles?: ProtectedRouteRole | ProtectedRouteRole[];
  /**
   * Whether to save return URL for redirect after login
   * @default true
   */
  saveReturnUrl?: boolean;
}

/**
 * Creates a beforeLoad function that protects routes based on auth and role requirements
 */
export function createProtectedGuard(options: ProtectedRouteOptions = {}) {
  const {
    requireAuth = true,
    roles,
    saveReturnUrl = true,
  } = options;

  return ({ context, location }: { context: unknown; location: { href: string } }) => {
    const auth = (context as { auth: AuthContextType }).auth;
    const storedUser = authLib.getStoredUser();

    // Prefer in-memory auth context, but fall back to persisted storage.
    // This avoids redirect races immediately after login.
    const isAuthenticated = auth?.isAuthenticated || authLib.isAuthenticated();
    const hasRole = (role: ProtectedRouteRole) =>
      Boolean(auth?.hasRole(role) || authLib.hasRole(auth?.user || storedUser, role));

    // Check authentication
    if (requireAuth && !isAuthenticated) {
      if (saveReturnUrl) {
        sessionStorage.setItem('returnUrl', location.href);
      }
      throw redirect({ to: '/login', replace: true });
    }

    // Check role requirements
    if (roles && isAuthenticated) {
      const requiredRoles = Array.isArray(roles) ? roles : [roles];
      const hasRequiredRole = requiredRoles.some((role) => hasRole(role));

      if (!hasRequiredRole) {
        sessionStorage.removeItem('returnUrl');
        try {
          // Captured for the unauthorized page so it can show the bay the user tried.
          sessionStorage.setItem('blockedPath', location.href);
        } catch {
          /* ignore storage errors */
        }
        throw redirect({ to: '/unauthorized', replace: true });
      }
    }
  };
}

/**
 * Pre-configured guards for each portal
 */
export const adminGuard = createProtectedGuard({ roles: 'ADMIN' });
export const operationsGuard = createProtectedGuard({ roles: 'OPERATIONS' });
export const clientGuard = createProtectedGuard({ roles: 'CLIENT' });
export const salesGuard = createProtectedGuard({ roles: 'SALES' });
export const gmGuard = createProtectedGuard({ roles: 'GM' });
